import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { familyService } from '../services';

const FamilyContext = createContext(null);
export const useFamilyContext = () => useContext(FamilyContext);
export const useFamilyStatus = () => {
  const ctx = useContext(FamilyContext);
  return { isFamilySetupComplete: ctx.isFamilySetupComplete, isLoading: ctx.isLoading };
};

export const FamilyProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [familyInfo, setFamilyInfo] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [isFamilySetupComplete, setIsFamilySetupComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkFamilySetup = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    try {
      const data = await familyService.getFamily();
      setFamilyInfo(data);
      setFamilyMembers(data.members || []);
      setIsFamilySetupComplete(true);
    } catch (err) {
      const status = err?.response?.status;
      // 404 = no family yet (correct REST semantic after PR#53)
      // 401 / 403 are handled globally by the Axios interceptor → sessionExpired modal
      if (status !== 404) {
        console.warn('Family fetch failed:', status);
      }
      setIsFamilySetupComplete(false);
      setFamilyMembers([]);
      setFamilyInfo(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) checkFamilySetup();
  }, [isAuthenticated, checkFamilySetup]);

  useEffect(() => {
    const onLogin = () => checkFamilySetup();
    const onStatusChange = () => checkFamilySetup();
    window.addEventListener('userLoggedIn', onLogin);
    window.addEventListener('familyStatusChanged', onStatusChange);
    return () => {
      window.removeEventListener('userLoggedIn', onLogin);
      window.removeEventListener('familyStatusChanged', onStatusChange);
    };
  }, [checkFamilySetup]);

  return (
    <FamilyContext.Provider value={{
      familyInfo, familyMembers, isFamilySetupComplete, isLoading,
      refreshFamilyStatus: checkFamilySetup,
    }}>
      {children}
    </FamilyContext.Provider>
  );
};
